/**
 * PerAuthorPostTemplateService — CRUD for the per-author per-platform
 * PostTemplate library at:
 *   projects/{pid}/authors/{authorId}/platforms/{platformId}/post_templates/{id}
 *
 * System fallback lives under
 * `system_platforms/{platformId}/post_templates/*`.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { stripUndefined } from '../utils/stripUndefined';
import type { PerAuthorPostTemplate, PostTemplate } from '../types';
import type { AuthorBasePath } from './AuthorPlatformService';

export type PerAuthorPostTemplateCreate = Omit<
  PerAuthorPostTemplate,
  'id' | 'createdAt' | 'updatedAt'
>;
export type PerAuthorPostTemplatePatch = Partial<
  Omit<PerAuthorPostTemplate, 'id' | 'createdAt'>
>;

export class PerAuthorPostTemplateService {
  constructor(private readonly basePath: AuthorBasePath = 'authors') {}

  private get db() {
    return CoreFirebaseService.getInstance().firestore;
  }

  private collectionRef(projectId: string, authorId: string, platformId: string) {
    return collection(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
      platformId,
      'post_templates',
    );
  }

  private docRef(
    projectId: string,
    authorId: string,
    platformId: string,
    templateId: string,
  ) {
    return doc(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
      platformId,
      'post_templates',
      templateId,
    );
  }

  async create(
    projectId: string,
    authorId: string,
    platformId: string,
    template: PerAuthorPostTemplateCreate,
  ): Promise<string> {
    const ref = this.collectionRef(projectId, authorId, platformId);
    const stripped = stripUndefined({
      ...template,
      authorId,
      platformId,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    const created = await addDoc(ref, stripped);
    return created.id;
  }

  async update(
    projectId: string,
    authorId: string,
    platformId: string,
    templateId: string,
    patch: PerAuthorPostTemplatePatch,
  ): Promise<void> {
    const stripped = stripUndefined({
      ...patch,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await updateDoc(
      this.docRef(projectId, authorId, platformId, templateId),
      stripped,
    );
  }

  async delete(
    projectId: string,
    authorId: string,
    platformId: string,
    templateId: string,
  ): Promise<void> {
    await deleteDoc(this.docRef(projectId, authorId, platformId, templateId));
  }

  /**
   * Import a system_platforms/{platformId}/post_templates/{templateId} doc into the per-author
   * overlay. No-op if a doc with the same id already exists in the
   * overlay (preserves user edits). Reads PlatformVoice for the
   * author×platform and uses its hashtag/emoji policy as default for
   * the new template's policy override slots when the system template
   * doesn't supply explicit values.
   *
   * Returns the imported template id or null when the system template
   * doesn't exist.
   */
  async importFromSystemCatalogue(
    projectId: string,
    authorId: string,
    platformId: string,
    systemTemplateId: string,
  ): Promise<string | null> {
    const systemRef = doc(this.db, 'system_platforms', platformId, 'post_templates', systemTemplateId);
    const systemSnap = await getDoc(systemRef);
    if (!systemSnap.exists()) return null;
    const systemTpl = systemSnap.data() as PostTemplate;

    const overlayRef = this.docRef(projectId, authorId, platformId, systemTpl.id);
    const overlaySnap = await getDoc(overlayRef);
    if (overlaySnap.exists()) return systemTpl.id; // already imported

    // Cascade hashtag/emoji policy default: prefer system value, fall
    // back to PlatformVoice, else absent (resolver chains to playbook
    // fallback at gen-time).
    const voiceRef = doc(
      this.db,
      'projects',
      projectId,
      this.basePath,
      authorId,
      'platforms',
      platformId,
      'voice',
      'voice',
    );
    const voiceSnap = await getDoc(voiceRef);
    const voice = voiceSnap.exists() ? (voiceSnap.data() as { hashtagPolicy?: string; emojiPolicy?: string }) : null;
    const hashtagPolicy = systemTpl.hashtagPolicy ?? voice?.hashtagPolicy;
    const emojiPolicy = systemTpl.emojiPolicy ?? voice?.emojiPolicy;

    const overlay = stripUndefined({
      ...systemTpl,
      authorId,
      platformId,
      baseTemplateId: systemTpl.id,
      scope: 'author',
      hashtagPolicy,
      emojiPolicy,
      createdAt: serverTimestamp() as any,
      updatedAt: serverTimestamp() as any,
    }) as Record<string, unknown>;
    await setDoc(overlayRef, overlay);
    return systemTpl.id;
  }
}

export const perAuthorPostTemplateService = new PerAuthorPostTemplateService('authors');
export const externalPerAuthorPostTemplateService = new PerAuthorPostTemplateService('external_authors');

export function getPerAuthorPostTemplateService(
  basePath?: AuthorBasePath,
): PerAuthorPostTemplateService {
  return basePath === 'external_authors'
    ? externalPerAuthorPostTemplateService
    : perAuthorPostTemplateService;
}
